import React, { useCallback, useEffect } from 'react';
import { useWizard } from 'use-wizard';

import { useAccount } from '@/hooks/useAccount';
import { AxonModule } from '@/modules/axon';
import { queryParamsModule } from '@/modules/queryParams';
import { preloadImage } from '@/utils/preloadImage';
import { preloadVideo } from '@/utils/preloadVideo';

export const useProxyWizard = (
  layoutStepsKeys: string[],
  layoutSteps: Record<string, { component: React.FC; images: string[] }>,
) => {
  const [step, wizard] = useWizard(layoutStepsKeys);
  const { user } = useAccount();
  const userId = user?.id;

  useEffect(() => {
    preloadStepImages(1);
  }, []);

  useEffect(() => {
    if (userId) {
      AxonModule.trackPageView(userId);
    }
  }, [userId]);

  const preloadStepImages = useCallback(
    (stepIndex: number) => {
      if (stepIndex < layoutStepsKeys.length) {
        const nextStepName = layoutStepsKeys[stepIndex] as
          | keyof typeof layoutSteps
          | undefined;

        if (nextStepName) {
          const images = layoutSteps[nextStepName].images;

          if (images.length > 0) {
            images.forEach((image) => {
              if (image.endsWith('.webm')) {
                preloadVideo(image);
              } else {
                preloadImage(image);
              }
            });
          }
        }
      }
    },
    [layoutSteps, layoutStepsKeys],
  );

  const proxyWizard = React.useMemo(() => {
    return new Proxy(wizard, {
      get(target, prop) {
        if (prop === 'goToStep') {
          return (step: string) => {
            if (step in layoutSteps) {
              target.goToStep(step);
            } else {
              step = layoutStepsKeys[0];
              target.goToStep(step);
            }

            const currentStepIndex = layoutStepsKeys.indexOf(step);

            queryParamsModule.setParam('step', step);
            AxonModule.trackPageView(userId);

            preloadStepImages(currentStepIndex + 1);
          };
        }

        if (prop === 'nextStep') {
          return () => {
            const currentStepIndex = target.stepIndex();
            const nextStepKey = layoutStepsKeys[currentStepIndex + 1];

            if (nextStepKey) {
              target.nextStep();
              queryParamsModule.setParam('step', nextStepKey);
              AxonModule.trackPageView(userId);

              preloadStepImages(currentStepIndex + 2);
            }
          };
        }

        return Reflect.get(target, prop);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard]);

  return {
    step,
    proxyWizard,
  } as const;
};
